import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { forensicReqs, forensicResults, forensicSteps } from '@/data';
import { Badge } from '@/components/ui/Badge';
import { SectionCard } from '@/components/ui/Card';
import { FieldGrid } from '@/components/ui/FieldGrid';
import { Btn } from '@/components/ui/Button';
import { PageHeader } from '@/components/layout/PageHeader';

export default function ForensicReq() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const FR = forensicReqs;
  const RESULTS = forensicResults;
  const STEPS = forensicSteps;
  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);
  const [catF, setCatF] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [selNo, setSelNo] = useState(FR[0].no);

  const statusTone: Record<string, string> = { fsReceived: 'neutral', fsAnalyzing: 'warning', fsDone: 'success', fsHold: 'danger' };
  const cats = ['catDrugF', 'catDigitalF', 'catDnaF', 'catPrintF', 'catDocF', 'catToxF'];
  const catColor: Record<string, string> = {
    catDrugF: '#0054A6', catDigitalF: '#1F8A5B', catDnaF: '#8A4FBE',
    catPrintF: '#C77B0A', catDocF: '#5B7A95', catToxF: '#A50F1D',
  };
  const catIcon: Record<string, string> = {
    catDrugF: 'bi-capsule', catDigitalF: 'bi-cpu', catDnaF: 'bi-diagram-2',
    catPrintF: 'bi-fingerprint', catDocF: 'bi-file-text', catToxF: 'bi-eyedropper',
  };

  const rows = FR.filter((r) =>
    (catF === 'all' || r.cat === catF) && (statusF === 'all' || r.status === statusF));
  const sel = FR.find((r) => r.no === selNo);
  const selResults = sel ? (RESULTS[sel.cat] || []) : [];

  const counts = {
    total: FR.length,
    analyzing: FR.filter((r) => r.status === 'fsAnalyzing').length,
    done: FR.filter((r) => r.status === 'fsDone').length,
    hold: FR.filter((r) => r.status === 'fsHold').length,
  };

  return (
    <div className="mp-page" data-screen-label="포렌식 의뢰">
      <PageHeader
        crumbs={[t('home'), t('mForensic'), t('mForensicReq')]}
        title={t('frTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('frSub')}</span></div>}
        actions={
          <Fragment>
            <Btn icon="bi-file-earmark-excel">{t('excel')}</Btn>
            <Btn kind="primary" icon="bi-plus-lg">{t('newRequest')}</Btn>
          </Fragment>
        }
      />

      <div className="mp-summary">
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('frTotal')}</span>
          <span className="mp-summary__num">{counts.total}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('frAnalyzingC')}</span>
          <span className="mp-summary__num mp-summary__num--warn">{counts.analyzing}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('frDoneC')}</span>
          <span className="mp-summary__num">{counts.done}<small>{t('items')}</small></span>
        </div>
        <div className="mp-summary__item">
          <span className="mp-summary__label">{t('frHoldC')}</span>
          <span className="mp-summary__num mp-summary__num--danger">{counts.hold}<small>{t('items')}</small></span>
        </div>
      </div>

      <div className="mp-filterbar">
        <label className="mp-filter">
          <span>{t('frField')}</span>
          <select value={catF} onChange={(e) => setCatF(e.target.value)}>
            <option value="all">{t('all')}</option>
            {cats.map((c) => <option key={c} value={c}>{t(c)}</option>)}
          </select>
        </label>
        <label className="mp-filter">
          <span>{t('status')}</span>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="all">{t('all')}</option>
            <option value="fsReceived">{t('fsReceived')}</option>
            <option value="fsAnalyzing">{t('fsAnalyzing')}</option>
            <option value="fsDone">{t('fsDone')}</option>
            <option value="fsHold">{t('fsHold')}</option>
          </select>
        </label>
        <Btn small icon="bi-arrow-counterclockwise" onClick={() => { setCatF('all'); setStatusF('all'); }}>{t('reset')}</Btn>
        <span className="mp-filterbar__count">{t('total')} <strong>{rows.length}</strong> {t('items')}</span>
      </div>

      <div className="mp-grid mp-grid--list-side">
        <SectionCard flush>
          <table className="mp-table mp-table--select">
            <thead>
              <tr>
                <th>{t('frNo')}</th>
                <th>{t('frField')}</th>
                <th>{t('frTarget')}</th>
                <th>{t('frType')}</th>
                <th>{t('relatedCase')}</th>
                <th>{t('frAnalyst')}</th>
                <th>{t('frProgress')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.no} className={selNo === r.no ? 'is-selected' : ''} onClick={() => setSelNo(r.no)}>
                  <td>
                    <span className="mp-caseno">{r.no}</span>
                    {r.pri === 'high' ? <span className="mp-pritag">{t('priHigh')}</span> : null}
                  </td>
                  <td>
                    <span className="mp-fieldtag">
                      <i className={'bi ' + catIcon[r.cat]} style={{ color: catColor[r.cat] }}></i>
                      {t(r.cat)}
                    </span>
                  </td>
                  <td className="mp-table__strong">{ln(r.targetKo, r.targetMn)}</td>
                  <td>{t(r.type)}</td>
                  <td>{r.caseNo}</td>
                  <td>{r.analyst}</td>
                  <td>
                    <span className="mp-progress">
                      <span className="mp-progress__bar"><span style={{ width: r.progress + '%' }}></span></span>
                      <span className="mp-progress__pct">{r.progress}%</span>
                    </span>
                  </td>
                  <td><Badge tone={statusTone[r.status] as 'neutral' | 'warning' | 'success' | 'danger'}>{t(r.status)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>

        <div className="mp-stack">
          <SectionCard title={t('detail')}>
            {sel ? (
              <div className="mp-stack mp-stack--tight">
                <div className="mp-nodedetail__name">{ln(sel.targetKo, sel.targetMn)}</div>
                <FieldGrid rows={[
                  [t('frNo'), <span className="mp-caseno">{sel.no}</span>],
                  [t('frField'), t(sel.cat)],
                  [t('frType'), t(sel.type)],
                  [t('evidenceNo'), sel.evNo],
                  [t('relatedCase'), sel.caseNo],
                  [t('frRequester'), sel.requester],
                  [t('frAnalyst'), sel.analyst],
                  [t('frDate'), sel.date],
                  [t('frPriority'), sel.pri === 'high' ? t('priHigh') : t('priNormal')],
                  [t('status'), t(sel.status)],
                ]} />
              </div>
            ) : null}
          </SectionCard>
          {selResults.length ? (
            <SectionCard title={t('frResults')}>
              <div className="mp-artgrid">
                {selResults.map((a, i) => (
                  <div key={i} className="mp-art">
                    <i className={'bi ' + a.icon}></i>
                    <span className="mp-art__num">{a.count}</span>
                    <span className="mp-art__lbl">{ln(a.labelKo, a.labelMn)}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          ) : null}
          <SectionCard title={t('frStepsTitle')} flush>
            <ol className="mp-timeline">
              {STEPS.map((s, i) => (
                <li key={i} className={i === STEPS.length - 1 ? 'is-last' : ''}>
                  <span className="mp-timeline__date">{s.date}</span>
                  <span className="mp-timeline__text">{ln(s.ko, s.mn)}</span>
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
